import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { format } from 'date-fns'
import { NavBar } from 'components/NavBar/NavBar'
import { AppDispatch, RootState } from 'store'
import { ColorVariant, Flex, PageWrapper } from 'styling/GlobalStyles'
import { Heading1, MediumText, NormalText, SmallText } from 'styling/TypographyV2'
import { ArrowLeft } from 'assets/icons/ArrowLeft'
import { getOperationalTeamDetails } from 'features/operationalManagement/operationalManagementSlice'
import {
  IOperationalTeamMemberAssignment,
  IOperationalTeamSupervisor,
  TEAM_TYPE_LABEL,
  getTeamTypeAccent,
} from 'features/operationalManagement/operationalManagement.types'
import {
  BackLink,
  EmployeeAvatar,
  EscalationArrow,
  EscalationChain,
  EscalationChip,
  EscalationSection,
  KpiGrid,
  MetaLabel,
  OutlineActionButton,
  PanelTitle,
  TeamHeroCard,
  TeamHeroIcon,
  TeamHeroMeta,
} from './Organogram.styled'
import { OptionsProps, Table } from 'components/Table/Table'
import { Badge } from 'components/Badge/Badge'
import { TeamsIcon, UserSilhouetteIcon } from './icons'
import { KpiCard } from './components/KpiCard'
import { AssignEmployeeModal } from './components/AssignEmployeeModal'
import { TeamHistoryModal } from './components/TeamHistoryModal'

const ShieldIcon = (
  <svg viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3Zm0 2.1 6 2.3v4.6c0 4-2.7 7.9-6 9-3.3-1.1-6-5-6-9V6.4l6-2.3Z'
      fill='currentColor'
    />
  </svg>
)

const AssignPlusIcon = (
  <svg viewBox='0 0 24 24' fill='none'>
    <path
      d='M10 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-6 6c0-2.5 3-4.5 6-4.5s6 2 6 4.5V19H4v-1Zm14-7h2v2h2v2h-2v2h-2v-2h-2v-2h2v-2Z'
      fill='currentColor'
    />
  </svg>
)

const ClockHistoryIcon = (
  <svg viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 4a8 8 0 1 0 7.75 6H17.7A6 6 0 1 1 12 6V3l4 4-4 4V8Zm-.75 3h1.5v5.25l4.5 2.67-.75 1.23L11.25 13V7Z'
      fill='currentColor'
    />
  </svg>
)

const formatSupervisorsList = (supervisors: IOperationalTeamSupervisor[]): string => {
  if (supervisors.length === 0) return 'Unassigned'
  return supervisors.map((s) => `${s.user.firstName} ${s.user.lastName}`).join(', ')
}

export const TeamDetails = () => {
  const { id } = useParams<{ id: string }>()
  const teamId = id ? Number(id) : NaN
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const theme = useTheme() as any
  const [assignOpen, setAssignOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  const details = useSelector((state: RootState) =>
    Number.isFinite(teamId) ? state.operationalManagement.teamDetailsById[teamId] : undefined,
  )
  const loading = useSelector((state: RootState) =>
    Number.isFinite(teamId)
      ? state.operationalManagement.loadingTeamDetailsIds.includes(teamId)
      : false,
  )

  useEffect(() => {
    if (Number.isFinite(teamId)) {
      dispatch(getOperationalTeamDetails(teamId))
    }
  }, [dispatch, teamId])

  const typeAccent = details ? getTeamTypeAccent(theme, details.type) : theme.colorOrange
  const typeLabel = details ? TEAM_TYPE_LABEL[details.type] : ''

  const members = details?.currentMembers ?? []
  const totalMembers = members.length

  const sortedEscalation = useMemo(() => {
    if (!details) return []
    return details.escalationChain.filter((lvl) => lvl.length > 0)
  }, [details])

  const memberTableOptions = useMemo<OptionsProps<IOperationalTeamMemberAssignment>[]>(
    () => [
      { header: 'ID', cell: () => '—' },
      {
        header: 'Employee',
        cell: (m) => {
          const user = m.user
          const userLabel = user ? `${user.firstName} ${user.lastName}` : 'Unknown'
          return (
            <Flex center gap='0.625rem'>
              <EmployeeAvatar>
                <UserSilhouetteIcon />
              </EmployeeAvatar>
              <span>{userLabel}</span>
            </Flex>
          )
        },
      },
      { header: 'Role', cell: (m) => m.user?.currentRole?.name ?? '—' },
      { header: 'Department', cell: (m) => m.user?.department?.name ?? '—' },
      {
        header: 'Since',
        cell: (m) => (m.startDate ? format(new Date(m.startDate), 'dd/MM/yyyy') : '—'),
      },
    ],
    [],
  )

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <BackLink
          role='button'
          tabIndex={0}
          aria-label='Back to teams'
          onClick={() => navigate('/organogram?tab=Teams')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              navigate('/organogram?tab=Teams')
            }
          }}
        >
          <ArrowLeft />
          Team details
        </BackLink>

        {!Number.isFinite(teamId) && (
          <NormalText colorVariant={ColorVariant.ERROR}>Invalid team id.</NormalText>
        )}

        {Number.isFinite(teamId) && loading && !details && (
          <NormalText colorVariant={ColorVariant.SECONDARY}>Loading team details…</NormalText>
        )}

        {Number.isFinite(teamId) && !loading && !details && (
          <NormalText colorVariant={ColorVariant.SECONDARY}>Team not found.</NormalText>
        )}

        {details && (
          <>
            <TeamHeroCard>
              <TeamHeroIcon>{ShieldIcon}</TeamHeroIcon>
              <div>
                <Flex center gap='0.75rem'>
                  <Heading1 uppercase>{details.name}</Heading1>
                  <Badge hexColor={typeAccent}>{typeLabel}</Badge>
                </Flex>
                <SmallText colorVariant={ColorVariant.SECONDARY} mt='0.25rem'>
                  {details.operationalSite?.name ?? '—'}
                </SmallText>
                <TeamHeroMeta>
                  <div>
                    <MetaLabel>Supervisors</MetaLabel>
                    <MediumText bold>{formatSupervisorsList(details.supervisors)}</MediumText>
                  </div>
                  <div>
                    <MetaLabel>Members</MetaLabel>
                    <MediumText bold>{totalMembers}</MediumText>
                  </div>
                  <div>
                    <MetaLabel>Parent team</MetaLabel>
                    <MediumText bold>{details.parentTeam?.name ?? '—'}</MediumText>
                  </div>
                </TeamHeroMeta>
              </div>
              <Flex gap='0.75rem'>
                <OutlineActionButton accent onClick={() => setAssignOpen(true)}>
                  {AssignPlusIcon}
                  Assign
                </OutlineActionButton>
                <OutlineActionButton onClick={() => setHistoryOpen(true)}>
                  {ClockHistoryIcon}
                  History
                </OutlineActionButton>
              </Flex>
            </TeamHeroCard>

            <KpiGrid
              style={{
                gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
                maxWidth: '360px',
              }}
            >
              <KpiCard
                icon={<TeamsIcon />}
                value={totalMembers}
                label='Total Members'
                accentColor={theme.blueChart}
              />
            </KpiGrid>

            <Flex column gap='0.75rem'>
              <MediumText bold colorVariant={ColorVariant.BASE}>
                Members
              </MediumText>
              <Table<IOperationalTeamMemberAssignment>
                cellKey='id'
                items={members}
                emptyListOptions={{ title: 'No active members.' }}
                options={memberTableOptions}
              />
            </Flex>

            <EscalationSection>
              <PanelTitle>Escalation chain</PanelTitle>
              {sortedEscalation.length === 0 ? (
                <SmallText colorVariant={ColorVariant.SECONDARY}>
                  No escalation chain defined.
                </SmallText>
              ) : (
                <EscalationChain>
                  {sortedEscalation.map((level, i) => (
                    <Flex center gap='0.5rem' key={i}>
                      <EscalationChip>{formatSupervisorsList(level)}</EscalationChip>
                      {i < sortedEscalation.length - 1 && <EscalationArrow>→</EscalationArrow>}
                    </Flex>
                  ))}
                </EscalationChain>
              )}
            </EscalationSection>

            {assignOpen && (
              <AssignEmployeeModal
                teamId={details.id}
                teamName={details.name}
                onClose={() => setAssignOpen(false)}
                onAssigned={() => {
                  dispatch(getOperationalTeamDetails(details.id))
                }}
              />
            )}

            {historyOpen && (
              <TeamHistoryModal
                teamName={details.name}
                onClose={() => setHistoryOpen(false)}
              />
            )}
          </>
        )}
      </PageWrapper>
    </>
  )
}
